import { AxiosError, AxiosInstance } from "axios";
import requests from "../../requests";
import { MessageErrorResponse } from "../../requests/@types";
import { route } from "../../shim/jsbridge";

const AUTHENTICATION_ERROR_MESSAGE =
  "Authentication credentials were not provided.";
const MAINTENANCE_MESSAGE = "MAINTENANCE MODE";
const LOGIN_URL = "/accounts/login";

const isMaintenanceMode = (
  data: string | object | MessageErrorResponse | null
) =>
  (typeof data === "string" && (data ?? "").includes(MAINTENANCE_MESSAGE)) ||
  (typeof data === "object" &&
    (JSON.stringify(data) ?? "").includes(MAINTENANCE_MESSAGE));

const pathOf = (url: unknown) =>
  typeof url === "string" && url
    ? new URL(url, window.location.origin).pathname
    : "";

export class AxiosApiWrapper {
  private isLoggingOut = false;
  public instance: AxiosInstance;
  public setHeader: (key: string, value: string) => void;
  public deleteHeader: (key: string) => void;
  public request: AxiosInstance["request"];
  public get: AxiosInstance["get"];
  public delete: AxiosInstance["delete"];
  public head: AxiosInstance["head"];
  public options: AxiosInstance["options"];
  public post: AxiosInstance["post"];
  public put: AxiosInstance["put"];
  public patch: AxiosInstance["patch"];

  constructor(config = {}) {
    const instance = requests(
      Object.assign({}, config, {
        xsrfCookieName: "csrftoken",
        xsrfHeaderName: "X-CSRFToken",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      })
    );

    this.instance = instance.instance;
    this.setHeader = instance.setHeader;
    this.deleteHeader = instance.deleteHeader;
    this.request = instance.request;
    this.get = instance.get;
    this.delete = instance.delete;
    this.head = instance.head;
    this.options = instance.options;
    this.post = instance.post;
    this.put = instance.put;
    this.patch = instance.patch;

    this.instance.interceptors.request.use(conf => {
      conf.headers["X-CSRFToken"] = window?.tcbase?.csrfToken();
      return conf;
    });

    this.instance.interceptors.response.use(
      response => {
        const requested = pathOf(response.config.url);
        const landedOn = pathOf(response.request?.responseURL);

        // The browser follows a login_required 302 itself, so a lost session
        // arrives as a 200 whose body is the login page's HTML. Logout lands
        // there by design.
        if (
          landedOn.startsWith(LOGIN_URL) &&
          landedOn !== requested &&
          requested !== pathOf(route("logout"))
        ) {
          this.logout();
          throw new AxiosError(
            "Redirected to login",
            AxiosError.ERR_BAD_RESPONSE,
            response.config,
            response.request
          );
        }

        return response;
      },
      async (error: AxiosError<MessageErrorResponse>) => {
        const { response: { status = 0, data = null } = {} } = error;

        if (status === 403 && data?.message === AUTHENTICATION_ERROR_MESSAGE) {
          this.logout();
        }

        if (status === 503 && isMaintenanceMode(data)) {
          window.location.reload();
        }

        throw error;
      }
    );
  }

  /**
   * Ends the session and sends the user to the login page.
   * @param next - return the user to the current page after login (default true)
   */
  public logout = ({ next = "" } = {}) => {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    const to = next ? route("login", {}, { next }) : route("login");
    this.post(route("logout"))
      .then(() => {
        window.location.href = to;
      })
      .catch(() => undefined);
  };
}
